import * as THREE from 'three';
import { GameLoop } from '../core';
import {
  Renderer,
  DirectPipeline,
  BloomPipeline
} from '../rendering';
import type { RenderPipeline } from '../rendering';
import { CameraRig } from '../camera';
import { InputManager } from '../input';
import {
  MainScene,
  PlanetSurfaceScene,
  StationInteriorScene
} from '../scenes';
import {
  UIRoot,
  HUDLayer,
  FadeOverlay,
  PromptLayer,
  MinimapLayer,
  CrosshairLayer,
  FactionBadgeLayer,
  ShipStatsHUD,
  AstronautStatsHUD,
  UnderwaterOverlay,
  RescueStatusHud,
  PauseMenuOverlay,
  InventoryOverlay,
  CurrencyBadge,
  ShopOverlay,
  MissionOverlay,
  SystemMapOverlay,
  WaypointHUD,
  JumpHud,
  DamageFlashLayer,
  PickupToastLayer,
  MiningToolHUD
} from '../ui';
import { NullNetworkClient } from '../networking';
import type { NetworkClient } from '../networking';
import { gameConfig } from '../config';
import { theme } from '../visual';
import { GameDirector, FactionHub } from '../gameplay/flow';
import { AudioManager } from '../audio';
import type { FactionSelection } from '../gameplay/factions';
import { PlayerStats } from '../gameplay/player';
import { Inventory } from '../gameplay/inventory';
import { PlayerWallet } from '../gameplay/wallet';
import { ShipUpgrades } from '../gameplay/upgrades';
import { SaveGameService } from '../gameplay/save';
import type { SaveData } from '../gameplay/save';
import {
  MissionEventBus,
  MissionManager,
  InventoryValueWatcher,
  buildTutorialMissions
} from '../gameplay/missions';
import { MissionTrackerHUD } from '../ui';
import { readPortalContext } from '../gameplay/portals';
import type { PortalContext } from '../gameplay/portals';
import { defaultSelection } from '../gameplay/factions';

export class Application {
  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: Renderer;
  private readonly pipeline: RenderPipeline;
  private readonly cameraRig: CameraRig;
  private readonly input: InputManager;
  private readonly ui: UIRoot;
  private readonly fadeOverlay: FadeOverlay;
  private readonly promptLayer: PromptLayer;
  private readonly minimapLayer: MinimapLayer;
  private readonly crosshairLayer: CrosshairLayer;
  private readonly audio: AudioManager;
  private readonly network: NetworkClient;
  private readonly loop: GameLoop;
  private readonly detachResize: () => void;
  private readonly container: HTMLElement;
  private director: GameDirector | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    container.appendChild(this.canvas);

    this.renderer = new Renderer(this.canvas, theme.toneMapping.exposure);
    this.pipeline = this.createPipeline();
    this.cameraRig = new CameraRig(window.innerWidth / window.innerHeight);
    this.input = new InputManager();
    this.input.setPointerLockTarget(this.canvas);
    this.canvas.addEventListener('click', this.handleCanvasClick);
    // Right-click is the ship's mining-beam fire button. Suppress the OS
    // context menu so the click reaches the input manager cleanly.
    this.canvas.addEventListener('contextmenu', this.handleContextMenu);

    this.ui = new UIRoot(container);
    this.ui.mountLayer(new HUDLayer().element);
    this.minimapLayer = new MinimapLayer();
    this.ui.mountLayer(this.minimapLayer.element);
    this.crosshairLayer = new CrosshairLayer();
    this.ui.mountLayer(this.crosshairLayer.element);
    this.promptLayer = new PromptLayer();
    this.ui.mountLayer(this.promptLayer.element);
    this.fadeOverlay = new FadeOverlay();
    this.ui.mountLayer(this.fadeOverlay.element);

    this.audio = new AudioManager();
    this.bindAudioUnlock();

    this.network = new NullNetworkClient();

    this.loop = new GameLoop(
      (dt) => this.update(dt),
      () => this.render(),
      gameConfig.simulationHz,
      gameConfig.maxFrameDelta
    );

    this.detachResize = this.bindResize();
  }

  async start(): Promise<void> {
    // Read the Vibejam continuity context once. When `portal=true` the
    // player just hopped in from another game's exit portal, so we
    // skip every intro overlay and land them straight on the ship.
    const portalContext = readPortalContext();
    const portalEntry = portalContext.portal;

    const { faction, savedData } = portalEntry
      ? this.buildPortalEntry(portalContext)
      : await this.buildIntroEntry();

    const stats = new PlayerStats(faction);
    // The tutorial demands a refuel run as the first objective, so the
    // ship must arrive with the tank dry enough to make that mission
    // beat meaningful — start at 30% fuel. Skipped when a save is
    // applied right after this (`saveService.apply`) since the saved
    // fuel value will overwrite via `stats.restore`.
    stats.consumeFuel(stats.fuelMax * 0.7);
    const wallet = new PlayerWallet(300);
    const shipInventory = new Inventory(20, 'Ship');
    const fpsInventory = new Inventory(6, 'Suit');
    const shipUpgrades = new ShipUpgrades();

    // Mission system — single bus shared by every emitter; manager
    // listens for tutorial-relevant events and exposes a read-only
    // snapshot to the HUD. Starts paused; `start()` is called after the
    // save service has had a chance to restore prior progress.
    const missionBus = new MissionEventBus();

    this.ui.mountLayer(new FactionBadgeLayer(faction).element);
    const shipStatsHUD = new ShipStatsHUD(stats);
    const astronautStatsHUD = new AstronautStatsHUD(stats);
    this.ui.mountLayer(shipStatsHUD.element);
    this.ui.mountLayer(astronautStatsHUD.element);

    // Underwater tint + oxygen bar — only visible while the FPS player's
    // head is below a planet's water surface. Driven by GameDirector.
    const underwaterOverlay = new UnderwaterOverlay(stats);
    this.ui.mountLayer(underwaterOverlay.element);

    // Recovery tug status card — visible while a rescue tug is travelling
    // toward the player. Driven by GameDirector.
    const rescueStatusHud = new RescueStatusHud();
    this.ui.mountLayer(rescueStatusHud.element);

    // Pause menu — ESC to toggle. Wired up in GameDirector once it owns
    // the overlay; the callbacks here just bridge to engine-level actions.
    const pauseMenuOverlay = new PauseMenuOverlay({
      onContinue: () => {}, // bound by GameDirector
      onRestart: () => window.location.reload(),
      onSave: () => {} // placeholder — persistence not implemented
    });
    this.ui.mountLayer(pauseMenuOverlay.element);

    // Damage feedback is driven entirely off PlayerStats — every callsite
    // that does stats.damageHull(...) feeds this layer automatically, so
    // no individual systems have to remember to fire the cue themselves.
    const damageFlash = new DamageFlashLayer(stats, this.audio);
    this.ui.mountLayer(damageFlash.element);

    // Pickup feedback diffs both inventories' snapshots — any addItem call
    // (asteroid loot, shop purchase, mission reward) shows a toast without
    // those callsites having to know the layer exists.
    const pickupToasts = new PickupToastLayer([shipInventory, fpsInventory]);
    this.ui.mountLayer(pickupToasts.element);

    // Tells the player when the FPS cutter is purchased and stowed/equipped.
    // Visibility per game-mode is gated by GameDirector.
    const miningToolHUD = new MiningToolHUD(shipUpgrades);
    this.ui.mountLayer(miningToolHUD.element);

    const currencyBadge = new CurrencyBadge(wallet);
    this.ui.mountLayer(currencyBadge.element);

    const inventoryOverlay = new InventoryOverlay(
      shipInventory,
      fpsInventory,
      stats,
      shipUpgrades
    );
    this.ui.mountLayer(inventoryOverlay.element);

    const shopOverlay = new ShopOverlay({
      wallet,
      shipInventory,
      fpsInventory,
      stats,
      shipUpgrades,
      faction,
      missionBus,
      onClose: () => {
        // Director reconciles mode/controls; just ask the overlay to close.
        shopOverlay.hide();
        this.director?.onShopClosedExternally();
      }
    });
    this.ui.mountLayer(shopOverlay.element);

    const missionOverlay = new MissionOverlay(() => {
      missionOverlay.hide();
      this.director?.onMissionsClosedExternally();
    });
    this.ui.mountLayer(missionOverlay.element);

    const waypointHUD = new WaypointHUD();
    this.ui.mountLayer(waypointHUD.element);

    const jumpHud = new JumpHud();
    this.ui.mountLayer(jumpHud.element);

    const systemMapOverlay = new SystemMapOverlay({
      getMapBodies: () => spaceScene.getMapBodies(),
      getPlayerPosition: () =>
        spaceScene.getPlayerShip()?.object3D.position ?? new THREE.Vector3(),
      getCurrentDestinationId: () =>
        this.director?.getCurrentDestinationId() ?? null,
      getMissionDestinationId: () =>
        this.director?.getMissionDestinationId() ?? null,
      onSelect: (id) => this.director?.setDestinationById(id),
      onClose: () => {
        systemMapOverlay.hide();
        this.director?.onSystemMapClosedExternally();
      }
    });
    this.ui.mountLayer(systemMapOverlay.element);

    const spaceScene = new MainScene({
      input: this.input,
      camera: this.cameraRig.camera,
      audio: this.audio,
      faction,
      stats,
      shipInventory,
      shipUpgrades,
      missionBus,
      portalContext
    });
    await spaceScene.load();

    const surfaceScene = new PlanetSurfaceScene({
      input: this.input,
      camera: this.cameraRig.camera,
      audio: this.audio,
      faction,
      stats
    });

    const stationScene = new StationInteriorScene({
      input: this.input,
      camera: this.cameraRig.camera,
      audio: this.audio,
      faction,
      stats
    });

    // Mission manager + tracker HUD. The manager's hooks need to reach
    // the tracker for completion banners; the tracker subscribes to the
    // manager inside its own constructor. We resolve the chicken/egg
    // with a closure-captured `let` — both are wired in the same scope
    // so neither owns the other directly.
    let trackerHud: MissionTrackerHUD;
    // Forward declaration: the watcher is built later because it
    // needs the bus, but the mission factory's `skipIf` paths read
    // its computed value. The closure resolves at call time, by which
    // point the watcher is alive.
    let inventoryValueWatcherRef: InventoryValueWatcher | null = null;
    const missionManager = new MissionManager(
      buildTutorialMissions({
        shipUpgrades,
        getInventoryValue: () => inventoryValueWatcherRef?.computeValue() ?? 0,
        getMode: () => this.director?.getMode() ?? 'space-flying',
        getFuelRatio: () =>
          stats.fuelMax > 0 ? stats.fuel / stats.fuelMax : 0
      }),
      missionBus,
      {
        onMissionStarted: (mission) => trackerHud.flashMissionStarted(mission),
        onMissionCompleted: (mission) => {
          trackerHud.flashMissionCompleted(mission);
          this.audio.playConfirm();
        },
        onStepCompleted: () => trackerHud.flashStepAdvance()
      }
    );
    trackerHud = new MissionTrackerHUD(missionManager);
    this.ui.mountLayer(trackerHud.element);

    this.director = new GameDirector({
      spaceScene,
      surfaceScene,
      stationScene,
      cameraRig: this.cameraRig,
      input: this.input,
      fadeOverlay: this.fadeOverlay,
      promptLayer: this.promptLayer,
      minimapLayer: this.minimapLayer,
      crosshairLayer: this.crosshairLayer,
      shipStatsHUD,
      astronautStatsHUD,
      inventoryOverlay,
      shopOverlay,
      missionOverlay,
      systemMapOverlay,
      waypointHUD,
      jumpHud,
      audio: this.audio,
      fpsInventory,
      shipUpgrades,
      miningToolHUD,
      underwaterOverlay,
      rescueStatusHud,
      pauseMenuOverlay,
      missionManager,
      missionBus,
      portalContext
    });
    // Save service knows how to read/write every gameplay subsystem.
    // It's wired in here so the pause menu's Save button can dump state
    // and so a Load Game can be applied right after construction.
    const saveService = new SaveGameService({
      stats,
      wallet,
      shipInventory,
      fpsInventory,
      upgrades: shipUpgrades,
      faction,
      spaceScene,
      surfaceScene,
      getMode: () => this.director?.getMode() ?? 'space-flying',
      missionManager,
      missionBus
    });

    // Inventory value watcher — broadcasts the running sellable value
    // of (ship + suit) so mission steps can gate on "you have enough
    // cargo for tool X". The forward-declared ref lets the mission
    // factory's `skipIf` callbacks read the live total.
    const inventoryValueWatcher = new InventoryValueWatcher(
      [shipInventory, fpsInventory],
      missionBus
    );
    inventoryValueWatcherRef = inventoryValueWatcher;

    // If the player picked a save in the intro, apply it now — every
    // system has been constructed but the loop hasn't started, so the
    // very first rendered frame already shows the loaded state.
    if (savedData) {
      saveService.apply(savedData);
      // The inventory restore fires before mission state restore, so
      // a value-gated step (e.g. "gather X cr of cargo") that's
      // already satisfied at save time wouldn't see the event in the
      // right order. Force a re-emit now so the manager evaluates the
      // predicate against the restored state.
      inventoryValueWatcher.refresh();
    }

    // Now that the service exists, route the pause menu's Save button
    // through it. The Restart button still hard-reloads the page.
    pauseMenuOverlay.setOnSave(() => saveService.download());

    await this.director.init();

    // Kick off the tutorial mission chain. Safe to call after restore —
    // if a save already advanced the chain, the manager's `start()`
    // is a no-op and emits the restored snapshot to subscribers.
    missionManager.start();

    // If the save put the player on a planet, route them through the
    // surface restoration AFTER the director's init wired up cameras
    // + audio. Falling back to space-flying is fine — the player just
    // ends up where the ship was saved.
    if (
      savedData?.surface &&
      (savedData.mode === 'surface-astronaut' || savedData.mode === 'surface-ship')
    ) {
      const ok = await this.director.restoreToSurface(
        savedData.surface,
        savedData.mode
      );
      if (!ok) {
        // Planet not found in the active sector — fall through to
        // space-flying with the ship at its saved space pose.
      }
    }

    this.loop.start();
  }

  private async runFactionHub(): Promise<import('../gameplay/flow').FactionHubResult> {
    const hub = new FactionHub({
      container: this.container,
      pipeline: this.pipeline,
      getAspect: () => window.innerWidth / window.innerHeight,
      // The full SaveGameService instance doesn't exist yet (the
      // gameplay systems aren't constructed). Validation is pure, so
      // the static parser handles it — same code path that the
      // instance method delegates to.
      parseSave: (raw) => SaveGameService.parse(raw)
    });
    const result = await hub.run();
    await hub.dispose();
    return result;
  }

  /** Standard intro path — runs the FactionHub + load-game flow. */
  private async buildIntroEntry(): Promise<{
    faction: FactionSelection;
    savedData: SaveData | null;
  }> {
    const hubResult = await this.runFactionHub();
    return { faction: hubResult.selection, savedData: hubResult.saveData };
  }

  /** Vibejam portal path — bypass the intro entirely so the player
   *  lands directly in the cockpit. The faction defaults to whatever
   *  the unlocked roster picks; an optional `?color=#hex` from the
   *  portal hand-off tints the ship for visual continuity. */
  private buildPortalEntry(ctx: PortalContext): {
    faction: FactionSelection;
    savedData: SaveData | null;
  } {
    const base = defaultSelection();
    const hex = parseHexColor(ctx.color);
    const faction: FactionSelection = hex !== null
      ? { factionId: base.factionId, primaryColor: hex }
      : base;
    return { faction, savedData: null };
  }

  stop(): void {
    this.loop.stop();
  }

  dispose(): void {
    this.stop();
    this.detachResize();
    this.canvas.removeEventListener('click', this.handleCanvasClick);
    this.canvas.removeEventListener('contextmenu', this.handleContextMenu);
    this.input.dispose();
    this.ui.dispose();
    this.pipeline.dispose();
    this.renderer.dispose();
    this.network.disconnect();
    this.canvas.remove();
  }

  private handleCanvasClick = (): void => {
    this.audio.unlock();
    if (this.director?.wantsPointerLock()) {
      this.input.requestPointerLock();
    }
  };

  private handleContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private bindAudioUnlock(): void {
    const unlock = (): void => {
      this.audio.unlock();
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('pointerdown', unlock);
    };
    document.addEventListener('click', unlock, { once: false });
    document.addEventListener('keydown', unlock, { once: false });
    document.addEventListener('pointerdown', unlock, { once: false });
  }

  private update(dt: number): void {
    if (this.director) {
      this.director.update(dt);
      this.director.getActiveScene().update(dt);
    }
    this.cameraRig.update(dt);
    this.input.endFrame();
  }

  private render(): void {
    if (!this.director) return;
    const scene = this.director.getActiveScene();
    this.pipeline.render(scene.root, this.cameraRig.camera);
  }

  private createPipeline(): RenderPipeline {
    const size = { width: window.innerWidth, height: window.innerHeight };
    if (theme.bloom.enabled) {
      return new BloomPipeline(this.renderer.webgl, size, {
        strength: theme.bloom.strength,
        radius: theme.bloom.radius,
        threshold: theme.bloom.threshold
      });
    }
    return new DirectPipeline(this.renderer.webgl);
  }

  private bindResize(): () => void {
    const handler = (): void => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      this.renderer.resize(width, height);
      this.pipeline.resize(width, height);
      this.cameraRig.setAspect(width / height);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }
}

/** Parse the portal `?color=` hand-off into a hex literal usable by
 *  FactionSelection. Accepts `#rrggbb`, `rrggbb`, or `0xrrggbb`. */
function parseHexColor(input: string | null): number | null {
  if (!input) return null;
  const trimmed = input.trim().replace(/^#/, '').replace(/^0x/i, '');
  if (!/^[0-9a-fA-F]{6}$/.test(trimmed)) return null;
  return parseInt(trimmed, 16);
}
